import React from 'react';
import Axios from 'axios';
import { cloneDeep, debounce } from "lodash";
import dotProp from 'dot-prop-immutable';
import FlowContext from './context/FlowContext';
import { default as trans } from './translate';

export default class WithFlow extends React.Component {
    constructor(props) {
        super(props);

        const { flowUrl, debounceTime = 500, flow = null, translate = trans } = this.props;
        this.flowUrl = flowUrl;
        this.debounceTime = debounceTime;
        this.debounce = null;
        this.cancelTokenSource = null;
        this.pendingRequests = 0;
        this.requestParams = null;
        this.translate = translate;

        this.state = {
            flow: flow,
            status: 'idle',
        };
    }

    componentDidMount() {
        this.doRequest();
    }

    doRequest = (params, status = 'loading') => {
        // Increase the count of pending requests by 1
        this.pendingRequests = this.pendingRequests + 1;

        // Save te params of this request to requestParams
        this.requestParams = params;

        // Cancel and delete a possible existing debounce
        this.debounce?.cancel();
        this.debounce = null;

        // Cancel a possible pending request
        this.cancelTokenSource?.cancel();

        // Create a new cancel token
        this.cancelTokenSource = Axios.CancelToken.source();

        this.setState({ status });
        Axios.put(
            this.flowUrl,
            { ...this.state.flow, ...params },
            { cancelToken: this.cancelTokenSource.token },
        ).then((response) => {
            if (response?.data) {
                if (!this.debounce) {
                    // If there are no debounced requests then update the flow state
                    this.setState({ flow: response.data });
                }
            } else {
                return Promise.reject(new Error('no data'));
            }
        }).catch((error) => {
            if (!Axios.isCancel(error)) {
                throw new Error(error);
            }
        }).finally(() => {
            // Decrease the count of pending requests by 1
            this.pendingRequests = this.pendingRequests - 1;

            if (this.pendingRequests === 0) {
                // If there are no pending requests
                // Reset the params of the current request
                this.requestParams = null;

                // Reset the status state to idle
                this.setState({ status: 'idle' });
            }
        });
    }

    updateState = (key, path, value, callback = () => { }) => {
        this.setState(
            (state) => {
                return { [key]: dotProp.set(cloneDeep(state?.[key]), path, value), };
            },
            callback
        );
    }

    updateFlow = (path, value, shouldUpdate = false, action, callback = () => { }) => {
        if (this.state.status === 'submitting' || this.state.status === 'navigating') return;

        this.updateState(
            'flow',
            path,
            value,
            () => {
                if (shouldUpdate) {
                    // Cancel a possible existing debounce and start a new debounce with the current params
                    this.debounce?.cancel();
                    this.debounce = debounce(() => this.doRequest({ action }), this.debounceTime);
                    this.debounce();

                    // Cancel a possible pending request
                    this.cancelTokenSource?.cancel();
                } else if (this.debounce) {
                    // If there is a debounce reset the delay of this debounce
                    this.debounce();

                    // Cancel a possible pending request
                    this.cancelTokenSource?.cancel();
                } else if (this.requestParams) {
                    // If there are requestParams for a pending request start an new debounce with these params
                    const params = cloneDeep(this.requestParams);
                    this.debounce?.cancel();
                    this.debounce = debounce(() => this.doRequest(params), this.debounceTime);
                    this.debounce();

                    // Cancel the pending request
                    this.cancelTokenSource?.cancel();
                }

                callback();
            }
        );
    }

    setStep = (step) => {
        this.doRequest({ action: 'navigate', navigateTo: step?.position }, 'navigating');
    }

    render() {
        return (
            <FlowContext.Provider
                value={{
                    doRequest: this.doRequest,
                    setStep: this.setStep,
                    updateFlow: this.updateFlow,
                    updateState: this.updateState,
                    translate: this.translate,
                }}
            >
                {this.props.children({
                    doRequest: this.doRequest,
                    setStep: this.setStep,
                    updateFlow: this.updateFlow,
                    updateState: this.updateState,
                    translate: this.translate,
                    ...this.state,
                })}
            </FlowContext.Provider>
        );
    }
}
